import { AudioSummaryActions } from '@modules/feedback/components/AudioSummaryActions';
import { AudioHighlightChip } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioHighlightChip';
import { AudioTranscriptPreview } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioTranscriptPreview';
import { AudioHighlightMetadata } from '@modules/feedback/components/AudioType/types';
import {
  generateKeywordSummaryUnitsMap,
  getCurrentPlayingHighlight,
  getLabelFromHighlight,
  getNextHighlightToPlay,
  getPrevHighlightToPlay,
  getReasonSummaryUnits,
  getStartEndTimeFromAudioSummaryMetadata,
  getUniqueHighlightMetadata,
  isKeywordSummaryAudioBeingPlayed,
  isSummaryHighlightMetadataType,
  trackAudioFeedbackEvents,
} from '@modules/feedback/components/AudioType/utils';
import { SummaryOrFeedbackLabel } from '@modules/feedback/components/SummaryOrFeedbackLabel';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import useFeedbackListStore from '@modules/feedback/store/useFeedbackListStore';
import { getDeletedEditPredictionLabelIDs } from '@modules/feedback/utils';
import { ArrowForwardRounded } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { SummaryMessageUnit, SummaryMetadata } from 'generated/graphql';
import { useState } from 'react';
import {
  Feedback,
  FeedbackLabel,
  FeedbackLabelType,
} from 'v2/core/entities/feedback';
import { AudioConversationMessage } from 'v2/core/entities/feedback/content';
import getFieldNameToDisplay from 'v2/views/components/charts/tooltip/getFieldNameToDisplay';
import { shallow } from 'zustand/shallow';
import { KeywordOrReasonSummaryListItem } from '../../KeywordsAndReasonsSummary/KeywordOrReasonSummaryListItem';

interface AudioSummaryPreviewProps {
  feedback: Feedback;
  highlightMetadata: AudioHighlightMetadata[];
  selectedLabels: FeedbackLabel[];
  allLabels: FeedbackLabel[];
  seek: number;
  audioConversation: AudioConversationMessage[];
  onExpandFeedbackDetails?: () => void;
  feedbackListViewType: FeedbackListViewType;
}

const AudioSummaryPreview = ({
  feedback,
  highlightMetadata,
  seek,
  onExpandFeedbackDetails,
  selectedLabels,
  audioConversation,
  allLabels,
  feedbackListViewType,
}: AudioSummaryPreviewProps) => {
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const editedRecord = editedRecords.find(
    (editedRecord) => editedRecord.id === feedback.id,
  );

  const {
    currentPlayingMsgIndex,
    setSelectedSeekJumpTime,
    setSummaryTimeToPlay,
  } = useFeedbackListStore((state) => {
    return {
      currentPlayingMsgIndex:
        state.feedbacks[feedback.id]?.currentPlayingMsgIndex ?? 0,
      setSelectedSeekJumpTime: state.setSelectedSeekJumpTime,
      setSummaryTimeToPlay: state.setSummaryTimeToPlay,
    };
  }, shallow);

  const [hoveredSummary, setHoveredSummary] = useState<string | null>(null);

  const hasSummaryHighlight = highlightMetadata.some((highlight) =>
    isSummaryHighlightMetadataType(highlight),
  );

  const deletedEditPredictionLabelIDs =
    getDeletedEditPredictionLabelIDs(editedRecord);

  // Handles the case wherein a selected label is removed by the user
  const filteredSelectedLabels = selectedLabels.filter(
    (label) => !deletedEditPredictionLabelIDs.includes(label.id),
  );

  const transcriptPreview = (
    <AudioTranscriptPreview
      feedback={feedback}
      selectedLabels={filteredSelectedLabels}
      onExpandFeedbackDetails={onExpandFeedbackDetails}
    />
  );

  if (!hasSummaryHighlight) {
    return transcriptPreview;
  }

  const highlightToPreview =
    getCurrentPlayingHighlight(highlightMetadata, seek, 0, true) ??
    getNextHighlightToPlay(highlightMetadata, seek, 0, true) ??
    getPrevHighlightToPlay(highlightMetadata, seek, 0, true);

  if (
    !highlightToPreview ||
    !isSummaryHighlightMetadataType(highlightToPreview)
  ) {
    return transcriptPreview;
  }

  const selectedLabel = getLabelFromHighlight(
    filteredSelectedLabels,
    highlightToPreview,
  );

  const renderActions = (summary: SummaryMessageUnit) => (
    <AudioSummaryActions
      feedback={feedback}
      selectedLabels={filteredSelectedLabels}
      summary={summary}
      labelID={selectedLabel?.id}
      summaryIndex={summaryUnits?.indexOf(summary) ?? 0}
    />
  );

  if (!selectedLabel) {
    return transcriptPreview;
  }

  const labelsHavingSummaries = getUniqueHighlightMetadata(
    highlightMetadata,
    true,
  );

  const summaryUnits =
    selectedLabel.type === FeedbackLabelType.Entity
      ? selectedLabel?.keywordSummary?.units
      : getReasonSummaryUnits(
          selectedLabel,
          generateKeywordSummaryUnitsMap(allLabels ?? []),
          audioConversation,
        );

  const jumpAudioToSummaryStartTime = (
    metadata: SummaryMetadata | undefined,
  ) => {
    const { startTime, endTime } = getStartEndTimeFromAudioSummaryMetadata(
      metadata,
      audioConversation,
    );
    setSelectedSeekJumpTime(startTime, feedback.id);
    setSummaryTimeToPlay({ startTime, endTime }, feedback.id);
    trackAudioFeedbackEvents({
      eventName: 'Audio Feedback - Summary List: Played Audio',
      feedbackID: feedback.id,
    });
  };

  const isSummaryBeingPlayed = (metadata: SummaryMetadata | undefined) => {
    return isKeywordSummaryAudioBeingPlayed(metadata, currentPlayingMsgIndex);
  };

  return (
    <Stack px={2} pb={2} spacing={1}>
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-start"
          columnGap={2}
          flexWrap="wrap"
          sx={{
            overflowX: 'hidden',
          }}
        >
          <Box sx={{ mb: 0.5 }}>
            <SummaryOrFeedbackLabel
              isAudioRecord
              feedbackID={feedback.id}
              isSummaryView
              label={
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    overflowX: 'hidden',
                  }}
                >
                  <Typography variant="body2">
                    Summary for {getFieldNameToDisplay(selectedLabel.type)}
                  </Typography>
                  <AudioHighlightChip
                    highlight={highlightToPreview}
                    maxWidth={500}
                    label={selectedLabel}
                  />
                </Stack>
              }
            />
          </Box>
          {onExpandFeedbackDetails && labelsHavingSummaries.length > 1 && (
            <Button
              onClick={onExpandFeedbackDetails}
              size="small"
              endIcon={<ArrowForwardRounded />}
              sx={{ mb: 0.5 }}
            >
              View All{' '}
              {feedbackListViewType === FeedbackListViewType.SAVED
                ? 'Saved '
                : ''}
              ({labelsHavingSummaries.length})
            </Button>
          )}
        </Stack>
      </Box>
      {summaryUnits?.length ? (
        summaryUnits.map((summary) => (
          <KeywordOrReasonSummaryListItem
            key={summary.text}
            summary={summary}
            summaryIndex={summaryUnits.indexOf(summary)}
            hoveredSummary={hoveredSummary}
            setHoveredSummary={setHoveredSummary}
            shouldAnimate={isSummaryBeingPlayed}
            onClick={jumpAudioToSummaryStartTime}
            feedback={feedback}
            labelID={selectedLabel.id}
            renderActions={renderActions}
            shouldRenderSavedSnippetsOnly={
              feedbackListViewType === FeedbackListViewType.SAVED
            }
          />
        ))
      ) : (
        <Typography variant="body2" color="text.secondary" px={1} pt={1}>
          No summary found.
        </Typography>
      )}
    </Stack>
  );
};

export default AudioSummaryPreview;
